import { HttpClient, API_CONFIG } from './httpClient';
import { User } from './models';

/**
 * Clase para manejar la autenticación con soporte para información de usuario
 */
export class AuthService {
  private static tokenKey = 'userToken';
  private static userKey = 'userData';

  /**
   * Método para iniciar sesión
   */
  static async login(email: string, password: string): Promise<boolean> {
    try {
      // Clear any existing auth data first
      this.logout();

      // Llamada directa a fetch para evitar dependencia circular con HttpClient
      const response = await fetch(`${API_CONFIG.baseUrl}/users/login`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        throw new Error('Error de autenticación');
      }

      const data = await response.json();

      if (!data.token) {
        throw new Error('El servidor no devolvió un token de autenticación');
      }

      // Store token
      localStorage.setItem(this.tokenKey, data.token);

      // Store user data if available in the response (new API format)
      if (data.user && typeof data.user === 'object' && data.user.id) {
        localStorage.setItem(this.userKey, JSON.stringify(data.user));
        console.log('User data stored from login response');
        return true;
      }

      // If we don't have user data, we need to get it
      // First check if we can extract user ID from token
      const userId = this.getUserId();

      if (!userId) {
        console.error('No user ID in token or response');
        this.logout();
        return false;
      }

      // Make API call to get user data
      try {
        console.log(`User ID extracted from token: ${userId}, fetching user data...`);

        // Llamada directa a fetch para evitar dependencia circular con HttpClient
        const userResponse = await fetch(`${API_CONFIG.baseUrl}/users/${userId}`, {
          method: 'GET',
          headers: {
            ...API_CONFIG.headers,
            'Authorization': `Bearer ${data.token}`
          }
        });

        if (!userResponse.ok) {
          console.error(`Failed to fetch user data: ${userResponse.status}`);
          this.logout();
          return false;
        }

        const userData = await userResponse.json();

        if (!userData || !userData.id) {
          console.error('Invalid user data received from API');
          this.logout();
          return false;
        }

        localStorage.setItem(this.userKey, JSON.stringify(userData));
        console.log('User data fetched and stored after login');
        return true;
      } catch (userError) {
        console.error('Error getting user data after login:', userError);
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Error en login:', error);
      this.logout(); // Ensure clean state
      return false;
    }
  }

  /**
   * Método para cerrar sesión
   */
  static logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  /**
   * Método para obtener el token
   */
  static getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Método para verificar si hay sesión activa
   */
  static isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Método para obtener los datos del usuario sin llamada a la API
   */
  static getUserData(): User | null {
    const userData = localStorage.getItem(this.userKey);
    if (userData) {
      try {
        return JSON.parse(userData) as User;
      } catch (error) {
        console.error('Error parsing user data from localStorage', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Verificar si el usuario tiene un rol específico
   */
  static hasRole(role: string): boolean {
    const userData = this.getUserData();
    return Boolean(userData &&
      userData.roles &&
      Array.isArray(userData.roles) &&
      userData.roles.includes(role));
  }

  /**
   * Verificar si el usuario es administrador
   */
  static isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Obtener el ID del usuario desde los datos almacenados o decodificando el token JWT
   */
  static getUserId(): string | null {
    // First try from stored user data
    const userData = this.getUserData();
    if (userData && userData.id) {
      return userData.id;
    }

    // If not available, try to extract from JWT token
    const token = this.getToken();
    if (token) {
      try {
        // JWT tokens are in format: header.payload.signature
        const parts = token.split('.');
        if (parts.length === 3) {
          // Decode the payload (middle part)
          const payload = JSON.parse(atob(parts[1]));
          // JWT might store user ID in different fields ('id', 'sub', 'userId', etc.)
          // Check common possibilities
          const userId = payload.id || payload.sub || payload.userId || payload.user_id;
          if (userId) {
            return userId;
          } else {
            console.error('No user ID found in token payload');
            // Invalid token state - force logout
            this.logout();
          }
        } else {
          console.error('Invalid JWT token format');
          // Invalid token format - force logout
          this.logout();
        }
      } catch (error) {
        console.error('Error extracting user ID from token:', error);
        // Token parsing error - force logout
        this.logout();
      }
    }

    return null;
  }

  /**
   * Obtener el nombre de usuario
   */
  static getUsername(): string | null {
    const userData = this.getUserData();
    return userData ? userData.username : null;
  }

  /**
   * Obtener información del usuario actual
   */
  static async getCurrentUser(): Promise<User> {
    // First try to get from localStorage
    const userData = this.getUserData();
    if (userData) {
      return userData;
    }

    // If not in localStorage, check if we have a valid token
    const token = this.getToken();
    if (!token) {
      this.logout(); // Ensure clean logout state
      throw new Error('No hay token de autenticación');
    }

    // Get userId - we need this for the API call
    const userId = this.getUserId();

    // If we can't get a user ID, getUserId() will already force a logout
    // but we should handle this case explicitly too
    if (!userId) {
      this.logout();
      throw new Error('No se pudo obtener el ID de usuario. Por favor, inicia sesión nuevamente.');
    }

    // Usamos HttpClient para hacer la llamada
    try {
      const data = await HttpClient.get<User>(`/users/${userId}`);

      // Store user data for future use
      localStorage.setItem(this.userKey, JSON.stringify(data));

      return data;
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);

      // Force logout on any error
      this.logout();

      throw new Error('Se produjo un error en la autenticación. Por favor, inicia sesión nuevamente.');
    }
  }

  /**
   * Actualizar el perfil del usuario
   */
  static async updateProfile(profileData: {
    username?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
    role?: string;
  }): Promise<boolean> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('No se pudo obtener el ID del usuario');
      }

      const updatedUser = await HttpClient.put<User>(`/users/${userId}`, profileData);

      // Actualizar los datos del usuario en el almacenamiento local
      const currentUserData = this.getUserData();
      if (currentUserData) {
        const updatedUserData = {
          ...currentUserData,
          username: updatedUser.username || currentUserData.username,
          email: updatedUser.email || currentUserData.email,
          roles: updatedUser.roles || currentUserData.roles
        };
        localStorage.setItem(this.userKey, JSON.stringify(updatedUserData));
      }

      return true;
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);

      // Asegurar que pasamos un error con mensaje claro
      if (error instanceof Error) {
        // Si ya es un Error con mensaje, lo pasamos directamente
        throw error;
      } else if (typeof error === 'string') {
        // Si es un string, lo convertimos en Error
        throw new Error(error);
      } else if (error && typeof error === 'object' && 'message' in error) {
        // Si es un objeto con propiedad message
        throw new Error(error.message as string);
      } else {
        // Caso por defecto
        throw new Error('Error desconocido al actualizar el perfil');
      }
    }
  }
}
